import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@core/lib/cn';
import { useLockBodyScroll } from '@core/lib/hooks/useLockBodyScroll';

export interface ModalProps {
  open: boolean;
  /** Called on Escape, backdrop click, and the close button. */
  onClose: () => void;
  /** Accessible name. Rendered as the heading and wired to aria-labelledby. */
  title: string;
  /** Optional supporting line, wired to aria-describedby. */
  description?: string;
  children?: ReactNode;
  /** Footer actions. On a sheet these sit in the thumb-reachable zone. */
  footer?: ReactNode;
  /**
   * 'sheet' slides up from the bottom edge — the right shape on a phone, because
   * the content and its actions land under the thumb. 'dialog' centres itself,
   * for short confirmations.
   */
  variant?: 'sheet' | 'dialog';
  /** Set false for destructive confirmations where a stray tap must not dismiss. */
  dismissOnBackdrop?: boolean;
  className?: string;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Focus-trapped overlay.
 *
 * Not <dialog>: its `showModal()` top-layer behaviour is still inconsistent across
 * the mobile browsers we target, and it fights the sheet animation. So the trap is
 * implemented here, and it has to do all four parts or it is not a trap:
 *
 *   1. Move focus INTO the overlay on open (first focusable, else the container).
 *   2. Keep Tab and Shift+Tab cycling inside it.
 *   3. Escape closes.
 *   4. Return focus to the element that opened it on close — otherwise a keyboard
 *      user lands back at the top of the document and has to re-find their place.
 *
 * `aria-hidden` is NOT set on the rest of the app; instead the overlay is
 * `aria-modal="true"`, which is the supported way to tell AT that content outside
 * is inert without mutating the whole tree.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  variant = 'sheet',
  dismissOnBackdrop = true,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const headingId = useId();
  const descriptionId = `${headingId}-description`;

  useLockBodyScroll(open);

  // Remember what had focus before we steal it, and give it back on close.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    return () => {
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  // Key handling lives on a document listener rather than a JSX onKeyDown prop.
  // Two reasons: a keydown handler on a non-interactive element is an
  // accessibility smell (and a lint error), and a document listener still catches
  // Escape if focus somehow ends up outside the panel — exactly the case where a
  // user is most stuck.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      const active = document.activeElement;
      // Wrap at both ends, and pull focus back in if it has escaped the panel.
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex',
        variant === 'sheet' ? 'items-end' : 'items-center justify-center p-4',
      )}
    >
      {/* Backdrop. A button, not a div with onClick, so it is a real control for
          pointer users; keyboard users use Escape and never reach it (tabIndex -1). */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={dismissOnBackdrop ? onClose : undefined}
        className="absolute inset-0 h-full w-full cursor-default bg-ink/50 animate-fade-in"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex max-h-[90dvh] w-full flex-col bg-surface shadow-card',
          variant === 'sheet'
            ? 'rounded-t-card animate-slide-up sm:mx-auto sm:max-w-app'
            : 'max-w-app rounded-card animate-fade-in',
          className,
        )}
      >
        <div className="flex items-start gap-3 border-b border-line p-4">
          <div className="min-w-0 flex-1">
            <h2 id={headingId} className="text-lg font-semibold text-ink">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-sm text-ink-muted">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-m-2 grid min-h-touch min-w-touch place-items-center rounded-control text-xl text-ink-muted hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">{children}</div>

        {footer ? (
          <div className="flex flex-col gap-2 border-t border-line p-4 pb-safe">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
